import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { format, isToday, isYesterday } from '@/utils/dates';
import { PaymentStatus, TransactionStatus } from '@seapoint/server/enums';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { Drawer, DrawerContent } from '../../../../design-system/drawer/drawer';
import { Stack } from '../../../../design-system/stack/stack';
import { getInstitutionDetails } from '../../../../pages/accounts/utils/get-institution-details';
import { getVendorLogo } from '../../../../pages/accounts/utils/get-vendor-details';
import { CategoryView, TransactionView } from '../../../../types/entity-types';
import { isBankingAccount } from '../../../../utils/accounts/is-banking-account';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getFormattedIban } from '../../../../utils/get-formatted-iban';
import { getFormattedSortCode } from '../../../../utils/get-formatted-sort-code';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';
import { EvidenceWrapper } from './evidence/evidence-wrapper';
import { Header } from './header/header';
import { PaymentInformation } from './payment-information/payment-information';
import { createBeneficiaryDetails } from './utils/beneficiary-utils';
import { getTransactionSummaryMetrics } from './utils/get-transaction-summary-metrics';
import { getAdjacentTransactionsFromFullList, handleTransactionKeyboardNavigation } from './utils/navigation';
import { VendorSection } from './vendor-section/vendor-section';

const mapTransactionStatusToPaymentStatus = (status: TransactionStatus | null): PaymentStatus => {
  switch (status) {
    case TransactionStatus.BOOKED:
      return PaymentStatus.SUCCEEDED;
    case TransactionStatus.PENDING:
      return PaymentStatus.PROCESSING;
    case TransactionStatus.FAILED:
      return PaymentStatus.FAILED;
    default:
      return PaymentStatus.PROCESSING;
  }
};

type TransactionDetailsDrawerProps = {
  isOpen: boolean;
  transaction: TransactionView | null;
  transactions: TransactionView[];
  categories: CategoryView[];
  isEditingVendorModalOpen: boolean;
  isNavVisible: boolean;
  onClose: () => void;
  onChangeVendor: () => void;
  onEditVendor: () => void;
  onChangeCategory: (categoryId: string) => void;
  onViewEvidence: (documentId: string) => void;
  onDownloadEvidence: (documentId: string) => void;
};

export const TransactionDetailsDrawer = ({
  isOpen,
  transaction,
  transactions,
  categories,
  isEditingVendorModalOpen,
  isNavVisible,
  onClose,
  onChangeVendor,
  onEditVendor,
  onChangeCategory,
  onViewEvidence,
  onDownloadEvidence,
}: TransactionDetailsDrawerProps) => {
  const analytics = useAnalytics();
  const { isPaymentTimelineEnabled } = useFlags<{ isPaymentTimelineEnabled: boolean }>();

  const { prev, next } = transaction
    ? getAdjacentTransactionsFromFullList(transactions, transaction.id)
    : { prev: undefined, next: undefined };

  useEffect(() => {
    if (isEditingVendorModalOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      handleTransactionKeyboardNavigation(event, isOpen, prev, next, (direction: 'previous' | 'next') => {
        analytics.track('transaction_drawer_navigated', {
          navigation_direction: direction,
          transaction_id: direction === 'previous' ? prev?.id : next?.id,
          navigation_source: 'keyboard',
        });
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, prev, next, isEditingVendorModalOpen, analytics]);

  const [vendorDescription, setVendorDescription] = useState<string>('');

  useEffect(() => {
    if (transaction?.vendor) {
      setVendorDescription(transaction.vendor.description ?? '');
    } else {
      setVendorDescription(
        transaction?.payee?.iban
          ? getFormattedIban(transaction.payee.iban)
          : `${transaction?.payee?.accountNumber ?? ''} • ${getFormattedSortCode(transaction?.payee?.sortCode ?? '')}`,
      );
    }
  }, [transaction?.vendor, transaction?.payee]);

  if (!transaction) {
    return null;
  }

  const {
    amount,
    currency,
    isCredit,
    fspDate,
    vendor,
    baseCurrency,
    baseCurrencyAmount,
    status,
    description,
    reference,
    type,
    account,
    payee,
    evidence,
    events,
  } = transaction;

  const vendorName = vendor?.name ?? payee?.name ?? '';
  const vendorLogo = getVendorLogo(vendor?.name, vendor?.logo) ?? '';

  const normalizedAmount = getNormalizedAmount({ amount, isCredit });
  const amountWithCurrency = getAmountWithCurrency({ amount: normalizedAmount, currency });

  const normalizedBaseCurrencyAmount = getNormalizedAmount({ amount: baseCurrencyAmount, isCredit });
  const amountWithBaseCurrency = getAmountWithCurrency({
    amount: normalizedBaseCurrencyAmount,
    currency: baseCurrency,
  });

  const beneficiary = createBeneficiaryDetails(transaction);

  const formattedDate = isToday(fspDate)
    ? `Today at ${format(fspDate, 'HH:mm')}`
    : isYesterday(fspDate)
      ? `Yesterday at ${format(fspDate, 'HH:mm')}`
      : format(fspDate, "d MMM yyyy 'at' HH:mm");

  const vendorTransactions = transactions.filter((t) => t.vendor?.id === vendor?.id);
  const metrics = getTransactionSummaryMetrics(vendorTransactions, currency);

  return (
    <Drawer open={isOpen} onOpenChange={onClose} direction="right" modal={false}>
      <DrawerContent aria-describedby={undefined}>
        <Header
          vendor={{ name: vendorName, logo: vendorLogo }}
          amount={amountWithCurrency}
          amountWithBaseCurrency={amountWithBaseCurrency}
          isCredit={isCredit}
          date={formattedDate}
          navigationConfig={isNavVisible ? { visible: true as const, prev, next } : { visible: false as const }}
        />
        <Stack
          space={8}
          className="scrollbar-hide overflow-y-auto p-[18px] [&::-webkit-scrollbar]:hidden"
          align="stretch"
        >
          <PaymentInformation
            type={type}
            status={status ?? ''}
            reference={reference ?? description}
            transactionId={transaction.id}
            description={description}
            cardNumber={isBankingAccount(account) ? (account?.accountInformation?.accountNo ?? '') : ''}
            events={isPaymentTimelineEnabled ? events : undefined}
            paymentStatus={isPaymentTimelineEnabled ? mapTransactionStatusToPaymentStatus(status) : undefined}
            account={(() => {
              const institutionDetails = account?.institution ? getInstitutionDetails(account.institution) : undefined;
              return {
                name: institutionDetails?.name ?? '',
                imageUrl: institutionDetails?.logo ?? '',
                currency: account?.currency ?? '',
              };
            })()}
          />
          <VendorSection
            isCredit={isCredit}
            beneficiary={beneficiary}
            vendorLogo={vendorLogo}
            vendorName={vendorName}
            vendorDescription={vendorDescription}
            onEditVendor={onEditVendor}
            onChangeVendor={onChangeVendor}
            onChangeCategory={onChangeCategory}
            category={vendor?.category}
            categories={categories}
            transactionSummaryMetrics={metrics}
          />
          {evidence && evidence.length > 0 && (
            <EvidenceWrapper
              evidence={evidence}
              onViewEvidence={onViewEvidence}
              onDownloadEvidence={onDownloadEvidence}
            />
          )}
        </Stack>
      </DrawerContent>
    </Drawer>
  );
};
